// KundaliReportsData.js — pure data/rules for the new Kundali report sections
// (Navatara Chakra, House/Office Activity Mapping via Ashtakavarga). Mirrors
// this codebase's established pattern of standalone data modules (see
// KuaNumberData.js, VastuChartVariants.js) — no React here, just tables and
// pure functions the UI components import.
//
// Every formula here is a STANDARD classical Vedic-astrology calculation,
// cross-checked against the client's own reference report (4 real people's
// worked examples) before being written — nothing here is guessed. The two
// exceptions, called out explicitly where used:
//   1. The reference report's "ASC" row in the Activity Mapping tables has
//      no standard classical formula (confirmed via research — Lagna is
//      only ever a *contributor* to the 7 planets' Bhinnashtakavarga
//      tables, it never gets a Bhinnashtakavarga table of its own). Per
//      explicit client decision, this is approximated using the Lagna
//      Lord's (Ascendant ruler's) own BAV row instead — real data, correct
//      scale, and astrologically grounded (the Lagna Lord represents the
//      Ascendant), just not a byte-for-byte match to the reference
//      software's own undocumented method.
//   2. Ruling Deities, Astro-Vastu Plant Remedy, and the Best Direction
//      Table (below) use the STANDARD classical tables instead — the
//      reference report's 4 worked examples weren't enough to reliably
//      reverse-engineer Ruling Deities/Plant Remedy specifically (Ruling
//      Deities in particular didn't reduce to a simple planet→deity lookup
//      across those examples), so these three ship with the textbook
//      classical assignments (cross-checked against multiple independent
//      classical sources) rather than a guess at the reference software's
//      own unconfirmed convention — client's explicit decision, made aware
//      this may not byte-for-byte match that other software.

// i18n: the read-only reference tables below (Ruling Deities, Best
// Direction, Plant Remedy, Navatara Chakra, Activity Mapping, Hit Theory)
// carry all 8 supported languages via small label maps + helper functions —
// the underlying NAKSHATRAS/NAKSHATRA_DEITIES/etc. arrays stay plain English
// since they're also used as lookup keys (e.g. GRAHA_PLANT_REMEDY[planet]),
// same pattern as KuaNumberData.js's kuaDir/kuaColors helpers.
import { pickLang } from './i18n/languageOptions';

const tr = (en, hi, gu, mr, bn, kn, ta, te) => ({ en, hi, gu, mr, bn, kn, ta, te });

// ── Nakshatras (27), each with its classical Vimshottari-cycle lord ────────
// Lord cycle is the standard fixed 9-planet sequence (Ketu, Venus, Sun, Moon,
// Mars, Rahu, Jupiter, Saturn, Mercury), repeating every 9 Nakshatras —
// verified against all 4 of the client's reference-report examples.
export const NAKSHATRAS = [
  { num: 1,  name: 'Ashwini',           lord: 'Ketu' },
  { num: 2,  name: 'Bharani',           lord: 'Venus' },
  { num: 3,  name: 'Krittika',          lord: 'Sun' },
  { num: 4,  name: 'Rohini',            lord: 'Moon' },
  { num: 5,  name: 'Mrigashira',        lord: 'Mars' },
  { num: 6,  name: 'Ardra',             lord: 'Rahu' },
  { num: 7,  name: 'Punarvasu',         lord: 'Jupiter' },
  { num: 8,  name: 'Pushya',            lord: 'Saturn' },
  { num: 9,  name: 'Ashlesha',          lord: 'Mercury' },
  { num: 10, name: 'Magha',             lord: 'Ketu' },
  { num: 11, name: 'Purva Phalguni',    lord: 'Venus' },
  { num: 12, name: 'Uttara Phalguni',   lord: 'Sun' },
  { num: 13, name: 'Hasta',             lord: 'Moon' },
  { num: 14, name: 'Chitra',            lord: 'Mars' },
  { num: 15, name: 'Swati',             lord: 'Rahu' },
  { num: 16, name: 'Vishakha',          lord: 'Jupiter' },
  { num: 17, name: 'Anuradha',          lord: 'Saturn' },
  { num: 18, name: 'Jyeshtha',          lord: 'Mercury' },
  { num: 19, name: 'Mula',              lord: 'Ketu' },
  { num: 20, name: 'Purva Ashadha',     lord: 'Venus' },
  { num: 21, name: 'Uttara Ashadha',    lord: 'Sun' },
  { num: 22, name: 'Shravana',          lord: 'Moon' },
  { num: 23, name: 'Dhanishta',         lord: 'Mars' },
  { num: 24, name: 'Shatabhisha',       lord: 'Rahu' },
  { num: 25, name: 'Purva Bhadrapada',  lord: 'Jupiter' },
  { num: 26, name: 'Uttara Bhadrapada', lord: 'Saturn' },
  { num: 27, name: 'Revati',            lord: 'Mercury' },
];

export const getNakshatra = (num) => NAKSHATRAS[((num - 1) % 27 + 27) % 27];

// ── Ruling Deities (classical Nakshatra-devata table) ──────────────────────
// Standard classical Vedic astrology assignment — one deity per Nakshatra,
// cross-checked against multiple independent classical references. NOTE:
// this could NOT be verified against the client's own 4 reference-report
// examples (see file header) — it's the standard textbook table, not a
// byte-for-byte match guarantee to whatever specific reference software the
// client may be comparing against. Client explicitly chose to proceed with
// this standard table regardless.
const NAKSHATRA_DEITIES = [
  'Ashwini Kumaras', 'Yama', 'Agni', 'Brahma', 'Chandra', 'Rudra', 'Aditi', 'Brihaspati', 'Nagas',
  'Pitris', 'Bhaga', 'Aryaman', 'Savitar', 'Vishvakarma', 'Vayu', 'Indra & Agni', 'Mitra', 'Indra',
  'Nirriti', 'Apah', 'Vishvadevas', 'Vishnu', 'Ashta Vasus', 'Varuna', 'Aja Ekapada', 'Ahirbudhnya', 'Pushan',
];
export const getNakshatraDeity = (num) => NAKSHATRA_DEITIES[((num - 1) % 27 + 27) % 27];

const NAKSHATRA_DEITY_LABELS = {
  'Ashwini Kumaras': tr('Ashwini Kumaras', 'अश्विनी कुमार', 'અશ્વિની કુમાર', 'अश्विनी कुमार', 'অশ্বিনী কুমার', 'ಅಶ್ವಿನಿ ಕುಮಾರರು', 'அஸ்வினி குமாரர்கள்', 'అశ్విని కుమారులు'),
  'Yama': tr('Yama', 'यम', 'યમ', 'यम', 'যম', 'ಯಮ', 'யமன்', 'యముడు'),
  'Agni': tr('Agni', 'अग्नि', 'અગ્નિ', 'अग्नी', 'অগ্নি', 'ಅಗ್ನಿ', 'அக்னி', 'అగ్ని'),
  'Brahma': tr('Brahma', 'ब्रह्मा', 'બ્રહ્મા', 'ब्रह्मा', 'ব্রহ্মা', 'ಬ್ರಹ್ಮ', 'பிரம்மா', 'బ్రహ్మ'),
  'Chandra': tr('Chandra', 'चंद्र', 'ચંદ્ર', 'चंद्र', 'চন্দ্র', 'ಚಂದ್ರ', 'சந்திரன்', 'చంద్రుడు'),
  'Rudra': tr('Rudra', 'रुद्र', 'રુદ્ર', 'रुद्र', 'রুদ্র', 'ರುದ್ರ', 'ருத்திரன்', 'రుద్రుడు'),
  'Aditi': tr('Aditi', 'अदिति', 'અદિતિ', 'अदिती', 'অদিতি', 'ಅದಿತಿ', 'அதிதி', 'అదితి'),
  'Brihaspati': tr('Brihaspati', 'बृहस्पति', 'બૃહસ્પતિ', 'बृहस्पती', 'বৃহস্পতি', 'ಬೃಹಸ್ಪತಿ', 'பிரகஸ்பதி', 'బృహస్పతి'),
  'Nagas': tr('Nagas', 'नाग', 'નાગ', 'नाग', 'নাগ', 'ನಾಗ', 'நாகர்கள்', 'నాగులు'),
  'Pitris': tr('Pitris', 'पितृ', 'પિતૃ', 'पितर', 'পিতৃ', 'ಪಿತೃಗಳು', 'பித்ருக்கள்', 'పితృదేవతలు'),
  'Bhaga': tr('Bhaga', 'भग', 'ભગ', 'भग', 'ভগ', 'ಭಗ', 'பகன்', 'భగుడు'),
  'Aryaman': tr('Aryaman', 'अर्यमन्', 'અર્યમન', 'अर्यमन', 'অর্যমা', 'ಅರ್ಯಮನ್', 'அரியமன்', 'అర్యమ'),
  'Savitar': tr('Savitar', 'सवितृ', 'સવિતૃ', 'सवितृ', 'সবিতৃ', 'ಸವಿತೃ', 'சவிதிர்', 'సవితృ'),
  'Vishvakarma': tr('Vishvakarma', 'विश्वकर्मा', 'વિશ્વકર્મા', 'विश्वकर्मा', 'বিশ্বকর্মা', 'ವಿಶ್ವಕರ್ಮ', 'விஸ்வகர்மா', 'విశ్వకర్మ'),
  'Vayu': tr('Vayu', 'वायु', 'વાયુ', 'वायू', 'বায়ু', 'ವಾಯು', 'வாயு', 'వాయువు'),
  'Indra & Agni': tr('Indra & Agni', 'इंद्र और अग्नि', 'ઇન્દ્ર અને અગ્નિ', 'इंद्र आणि अग्नी', 'ইন্দ্র ও অগ্নি', 'ಇಂದ್ರ ಮತ್ತು ಅಗ್ನಿ', 'இந்திரனும் அக்னியும்', 'ఇంద్రుడు మరియు అగ్ని'),
  'Mitra': tr('Mitra', 'मित्र', 'મિત્ર', 'मित्र', 'মিত্র', 'ಮಿತ್ರ', 'மித்திரன்', 'మిత్రుడు'),
  'Indra': tr('Indra', 'इंद्र', 'ઇન્દ્ર', 'इंद्र', 'ইন্দ্র', 'ಇಂದ್ರ', 'இந்திரன்', 'ఇంద్రుడు'),
  'Nirriti': tr('Nirriti', 'निऋति', 'નિરૃતિ', 'निऋती', 'নিঋতি', 'ನಿರೃತಿ', 'நிருதி', 'నిరృతి'),
  'Apah': tr('Apah', 'आपः', 'આપઃ', 'आपः', 'আপঃ', 'ಆಪಃ', 'ஆபஹ்', 'ఆపః'),
  'Vishvadevas': tr('Vishvadevas', 'विश्वदेव', 'વિશ્વદેવ', 'विश्वदेव', 'বিশ্বদেব', 'ವಿಶ್ವದೇವರು', 'விஸ்வதேவர்கள்', 'విశ్వదేవులు'),
  'Vishnu': tr('Vishnu', 'विष्णु', 'વિષ્ણુ', 'विष्णू', 'বিষ্ণু', 'ವಿಷ್ಣು', 'விஷ்ணு', 'విష్ణువు'),
  'Ashta Vasus': tr('Ashta Vasus', 'अष्ट वसु', 'અષ્ટ વસુ', 'अष्ट वसू', 'অষ্ট বসু', 'ಅಷ್ಟ ವಸುಗಳು', 'அஷ்ட வசுக்கள்', 'అష్ట వసువులు'),
  'Varuna': tr('Varuna', 'वरुण', 'વરુણ', 'वरुण', 'বরুণ', 'ವರುಣ', 'வருணன்', 'వరుణుడు'),
  'Aja Ekapada': tr('Aja Ekapada', 'अज एकपाद', 'અજ એકપાદ', 'अज एकपाद', 'অজ একপাদ', 'ಅಜ ಏಕಪಾದ', 'அஜ ஏகபாதன்', 'అజ ఏకపాద'),
  'Ahirbudhnya': tr('Ahirbudhnya', 'अहिर्बुध्न्य', 'અહિર્બુધ્ન્ય', 'अहिर्बुध्न्य', 'অহির্বুধ্ন্য', 'ಅಹಿರ್ಬುಧ್ನ್ಯ', 'அஹிர்புத்ன்யா', 'అహిర్బుధ్న్య'),
  'Pushan': tr('Pushan', 'पूषन्', 'પૂષન', 'पूषन', 'পূষণ', 'ಪೂಷನ್', 'பூஷன்', 'పూషన్'),
};
export const deityLabel = (deity, lang = 'en') => pickLang(NAKSHATRA_DEITY_LABELS[deity], lang) || deity;

// ── Best Direction Table (classical Nakshatra → house-facing direction) ────
// Traditional system using only the 4 cardinal directions (not the
// intercardinal ones) — standard classical table, verified consistent
// across independent sources.
const NAKSHATRA_BEST_DIRECTION = [
  'West', 'South', 'East', 'North', 'South', 'West', 'East', 'West', 'North',
  'West', 'South', 'East', 'North', 'South', 'West', 'East', 'West', 'North',
  'West', 'South', 'East', 'North', 'South', 'West', 'East', 'West', 'North',
];
export const getNakshatraBestDirection = (num) => NAKSHATRA_BEST_DIRECTION[((num - 1) % 27 + 27) % 27];

// Shared 8-direction label map — reused for the Best Direction table and the
// Plant Remedy table's Vastu placement column.
const DIRECTION_LABELS = {
  'North':      tr('North', 'उत्तर', 'ઉત્તર', 'उत्तर', 'উত্তর', 'ಉತ್ತರ', 'வடக்கு', 'ఉత్తరం'),
  'South':      tr('South', 'दक्षिण', 'દક્ષિણ', 'दक्षिण', 'দক্ষিণ', 'ದಕ್ಷಿಣ', 'தெற்கு', 'దక్షిణం'),
  'East':       tr('East', 'पूर्व', 'પૂર્વ', 'पूर्व', 'পূর্ব', 'ಪೂರ್ವ', 'கிழக்கு', 'తూర్పు'),
  'West':       tr('West', 'पश्चिम', 'પશ્ચિમ', 'पश्चिम', 'পশ্চিম', 'ಪಶ್ಚಿಮ', 'மேற்கு', 'పశ్చిమం'),
  'North-East': tr('North-East', 'उत्तर-पूर्व', 'ઉત્તર-પૂર્વ', 'ईशान्य', 'উত্তর-পূর্ব', 'ಈಶಾನ್ಯ', 'வடகிழக்கு', 'ఈశాన్యం'),
  'North-West': tr('North-West', 'उत्तर-पश्चिम', 'ઉત્તર-પશ્ચિમ', 'वायव्य', 'উত্তর-পশ্চিম', 'ವಾಯವ್ಯ', 'வடமேற்கு', 'వాయవ్యం'),
  'South-East': tr('South-East', 'दक्षिण-पूर्व', 'દક્ષિણ-પૂર્વ', 'आग्नेय', 'দক্ষিণ-পূর্ব', 'ಆಗ್ನೇಯ', 'தென்கிழக்கு', 'ఆగ్నేయం'),
  'South-West': tr('South-West', 'दक्षिण-पश्चिम', 'દક્ષિણ-પશ્ચિમ', 'नैऋत्य', 'দক্ষিণ-পশ্চিম', 'ನೈಋತ್ಯ', 'தென்மேற்கு', 'నైరుతి'),
  'Middle (Brahmasthan)': tr('Middle (Brahmasthan)', 'मध्य (ब्रह्मस्थान)', 'મધ્ય (બ્રહ્મસ્થાન)', 'मध्य (ब्रह्मस्थान)', 'মধ্য (ব্রহ্মস্থান)', 'ಮಧ್ಯ (ಬ್ರಹ್ಮಸ್ಥಾನ)', 'மையம் (பிரம்மஸ்தானம்)', 'మధ్య (బ్రహ్మస్థానం)'),
};
export const directionLabel = (dir, lang = 'en') => pickLang(DIRECTION_LABELS[dir], lang) || dir;

// ── Astro-Vastu Plant Remedy (classical Navagraha Vatika) ──────────────────
// Each of the 9 Grahas has one traditional remedy plant/tree and a Vastu
// placement direction — the classical "Navagraha Vatika" (nine-planet
// garden) system, standard across classical/traditional sources.
export const GRAHA_PLANT_REMEDY = {
  Sun:     { plant: 'Aak / Shwetark (Calotropis gigantea)',  direction: 'Middle (Brahmasthan)' },
  Moon:    { plant: 'Palash (Butea monosperma)',             direction: 'South-East' },
  Mars:    { plant: 'Khair (Acacia catechu)',                direction: 'South' },
  Mercury: { plant: 'Apamarga (Achyranthes aspera)',         direction: 'North' },
  Jupiter: { plant: 'Peepal / Ashvath (Ficus religiosa)',    direction: 'North-East' },
  Venus:   { plant: 'Gular / Anjeer (Ficus racemosa)',       direction: 'East' },
  Saturn:  { plant: 'Shami (Prosopis cineraria)',            direction: 'West' },
  Rahu:    { plant: 'Durva Grass (Cynodon dactylon)',        direction: 'South-West' },
  Ketu:    { plant: 'Darbha / Kush (Desmostachya)',          direction: 'North-West' },
};

const GRAHA_PLANT_LABELS = {
  Sun:     tr('Aak / Shwetark (Calotropis gigantea)', 'आक / श्वेतार्क (Calotropis gigantea)', 'આક / શ્વેતાર્ક (Calotropis gigantea)', 'आक / श्वेतार्क (Calotropis gigantea)', 'আক / শ্বেতার্ক (Calotropis gigantea)', 'ಆಕ್ / ಶ್ವೇತಾರ್ಕ (Calotropis gigantea)', 'ஆக் / ஸ்வேதார்க் (Calotropis gigantea)', 'ఆక్ / శ్వేతార్క్ (Calotropis gigantea)'),
  Moon:    tr('Palash (Butea monosperma)', 'पलाश (Butea monosperma)', 'પલાશ (Butea monosperma)', 'पळस (Butea monosperma)', 'পলাশ (Butea monosperma)', 'ಪಲಾಶ್ (Butea monosperma)', 'பலாஷ் (Butea monosperma)', 'పలాశ్ (Butea monosperma)'),
  Mars:    tr('Khair (Acacia catechu)', 'खैर (Acacia catechu)', 'ખેર (Acacia catechu)', 'खैर (Acacia catechu)', 'খয়ের (Acacia catechu)', 'ಖೈರ್ (Acacia catechu)', 'கைர் (Acacia catechu)', 'ఖైర్ (Acacia catechu)'),
  Mercury: tr('Apamarga (Achyranthes aspera)', 'अपामार्ग (Achyranthes aspera)', 'અપામાર્ગ (Achyranthes aspera)', 'अपामार्ग (Achyranthes aspera)', 'অপামার্গ (Achyranthes aspera)', 'ಅಪಾಮಾರ್ಗ (Achyranthes aspera)', 'அபாமார்க் (Achyranthes aspera)', 'అపామార్గ (Achyranthes aspera)'),
  Jupiter: tr('Peepal / Ashvath (Ficus religiosa)', 'पीपल / अश्वत्थ (Ficus religiosa)', 'પીપળ / અશ્વત્થ (Ficus religiosa)', 'पिंपळ / अश्वत्थ (Ficus religiosa)', 'পিপল / অশ্বত্থ (Ficus religiosa)', 'ಪೀಪಲ್ / ಅಶ್ವತ್ಥ (Ficus religiosa)', 'அரசமரம் / அஸ்வத்தம் (Ficus religiosa)', 'రావి / అశ్వత్థ (Ficus religiosa)'),
  Venus:   tr('Gular / Anjeer (Ficus racemosa)', 'गूलर / अंजीर (Ficus racemosa)', 'ગુલર / અંજીર (Ficus racemosa)', 'उंबर / अंजीर (Ficus racemosa)', 'গুলার / আঞ্জির (Ficus racemosa)', 'ಗೂಲರ್ / ಅಂಜೀರ (Ficus racemosa)', 'கூலர் / அத்தி (Ficus racemosa)', 'మేడి / అంజీర్ (Ficus racemosa)'),
  Saturn:  tr('Shami (Prosopis cineraria)', 'शमी (Prosopis cineraria)', 'શમી (Prosopis cineraria)', 'शमी (Prosopis cineraria)', 'শমী (Prosopis cineraria)', 'ಶಮಿ (Prosopis cineraria)', 'சாமி (Prosopis cineraria)', 'జమ్మి (Prosopis cineraria)'),
  Rahu:    tr('Durva Grass (Cynodon dactylon)', 'दूर्वा घास (Cynodon dactylon)', 'દૂર્વા ઘાસ (Cynodon dactylon)', 'दूर्वा गवत (Cynodon dactylon)', 'দূর্বা ঘাস (Cynodon dactylon)', 'ದೂರ್ವಾ ಹುಲ್ಲು (Cynodon dactylon)', 'தூர்வா புல் (Cynodon dactylon)', 'గరిక పచ్చ (Cynodon dactylon)'),
  Ketu:    tr('Darbha / Kush (Desmostachya)', 'दर्भ / कुश (Desmostachya)', 'દર્ભ / કુશ (Desmostachya)', 'दर्भ / कुश (Desmostachya)', 'দর্ভ / কুশ (Desmostachya)', 'ದರ್ಭ / ಕುಶ (Desmostachya)', 'தர்பை / குசம் (Desmostachya)', 'దర్భ / కుశ (Desmostachya)'),
};
export const plantLabel = (planet, lang = 'en') => pickLang(GRAHA_PLANT_LABELS[planet], lang) || GRAHA_PLANT_REMEDY[planet]?.plant || planet;

// ── Navatara Chakra (9-fold Tara cycle from the birth/Janma Nakshatra) ─────
// Classical Tara Bala system: starting from the Janma Nakshatra, every group
// of 3 Nakshatras 9 apart forms one Tara category. Formula verified against
// all 4 of the client's reference-report examples (exact Nakshatra-triplet
// match in every case).
const NAVATARA_CATEGORIES = [
  { key: 'janma',      label: 'Janma (Birth)' },
  { key: 'sampat',     label: 'Sampat (Wealth)' },
  { key: 'vipat',      label: 'Vipat (Dangers)' },
  { key: 'kshema',     label: 'Kshema (Healing)' },
  { key: 'pratyak',    label: 'Pratyak (Obstacles)' },
  { key: 'saadhana',   label: 'Saadhana (Achievements)' },
  { key: 'nidhana',    label: 'Nidhana (Death)' },
  { key: 'mitra',      label: 'Mitra (Friend)' },
  { key: 'paramMitra', label: 'ParamMitra (Very Good Friend)' },
];

const NAVATARA_LABELS = {
  janma:      tr('Janma (Birth)', 'जन्म (जन्म)', 'જન્મ (જન્મ)', 'जन्म (जन्म)', 'জন্ম (জন্ম)', 'ಜನ್ಮ (ಜನ್ಮ)', 'ஜன்ம (பிறப்பு)', 'జన్మ (జననం)'),
  sampat:     tr('Sampat (Wealth)', 'संपत् (धन)', 'સંપત (ધન)', 'संपत् (संपत्ती)', 'সম্পৎ (সম্পদ)', 'ಸಂಪತ್ (ಸಂಪತ್ತು)', 'சம்பத் (செல்வம்)', 'సంపత్ (సంపద)'),
  vipat:      tr('Vipat (Dangers)', 'विपत् (संकट)', 'વિપત (સંકટ)', 'विपत् (संकट)', 'বিপৎ (বিপদ)', 'ವಿಪತ್ (ಅಪಾಯ)', 'விபத் (ஆபத்து)', 'విపత్ (ప్రమాదాలు)'),
  kshema:     tr('Kshema (Healing)', 'क्षेम (उपचार)', 'ક્ષેમ (ઉપચાર)', 'क्षेम (उपचार)', 'ক্ষেম (আরোগ্য)', 'ಕ್ಷೇಮ (ಚಿಕಿತ್ಸೆ)', 'க்ஷேம (குணப்படுத்துதல்)', 'క్షేమ (వైద్యం)'),
  pratyak:    tr('Pratyak (Obstacles)', 'प्रत्यक् (बाधाएँ)', 'પ્રત્યક (અવરોધ)', 'प्रत्यक् (अडथळे)', 'প্রত্যক (বাধা)', 'ಪ್ರತ್ಯಕ್ (ಅಡಚಣೆ)', 'ப்ரத்யக் (தடைகள்)', 'ప్రత్యక్ (అడ్డంకులు)'),
  saadhana:   tr('Saadhana (Achievements)', 'साधना (उपलब्धियाँ)', 'સાધના (સિદ્ધિ)', 'साधना (सिद्धी)', 'সাধনা (সাফল্য)', 'ಸಾಧನಾ (ಸಾಧನೆಗಳು)', 'சாதனா (சாதனைகள்)', 'సాధన (సాధనలు)'),
  nidhana:    tr('Nidhana (Death)', 'निधन (मृत्यु)', 'નિધન (મૃત્યુ)', 'निधन (मृत्यू)', 'নিধন (মৃত্যু)', 'ನಿಧನ (ಮರಣ)', 'நிதன (மரணம்)', 'నిధన (మరణం)'),
  mitra:      tr('Mitra (Friend)', 'मित्र (मित्र)', 'મિત્ર (મિત્ર)', 'मित्र (मित्र)', 'মিত্র (বন্ধু)', 'ಮಿತ್ರ (ಸ್ನೇಹಿತ)', 'மித்ர (நண்பன்)', 'మిత్ర (మిత్రుడు)'),
  paramMitra: tr('ParamMitra (Very Good Friend)', 'परम मित्र (घनिष्ठ मित्र)', 'પરમ મિત્ર (ઘનિષ્ઠ મિત્ર)', 'परम मित्र (घनिष्ठ मित्र)', 'পরম মিত্র (ঘনিষ্ঠ বন্ধু)', 'ಪರಮ ಮಿತ್ರ (ಆಪ್ತ ಸ್ನೇಹಿತ)', 'பரம மித்ர (நெருங்கிய நண்பன்)', 'పరమ మిత్ర (ఆప్త మిత్రుడు)'),
};
export const navataraLabel = (key, lang = 'en') => pickLang(NAVATARA_LABELS[key], lang) || key;

/**
 * @param {number} janmaNakshatraNum 1-27 — the birth (Moon) Nakshatra.
 * @returns {Array<{ number: number, key: string, label: string, lord: string, nakshatras: string[] }>}
 */
export const computeNavataraChakra = (janmaNakshatraNum) => {
  if (!janmaNakshatraNum) return [];
  return NAVATARA_CATEGORIES.map((cat, i) => {
    const triplet = [0, 1, 2].map((k) => getNakshatra(janmaNakshatraNum + i + 9 * k).name);
    return {
      number: i + 1,
      key: cat.key,
      label: cat.label,
      lord: getNakshatra(janmaNakshatraNum + i).lord,
      nakshatras: triplet,
    };
  });
};

// ── Zodiac sign → direction group (for the Ashtakavarga-based mapping
// tables) — this exact 1/5/9, 3/7/11, 4/8/12, 2/6/10 grouping is printed
// directly as the column headers in the client's reference report, so it's
// taken verbatim, not inferred. Sign numbers: 1=Aries … 12=Pisces.
export const RASHI_EN = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

export const DIRECTION_GROUPS = {
  east:  { label: 'East Zodiac (1,5,9)',    signs: [1, 5, 9] },
  west:  { label: 'West Zodiac (3,7,11)',   signs: [3, 7, 11] },
  north: { label: 'North Zodiac (4,8,12)',  signs: [4, 8, 12] },
  south: { label: 'South Zodiac (2,6,10)',  signs: [2, 6, 10] },
};

const DIRECTION_GROUP_LABELS = {
  east:  tr('East Zodiac (1,5,9)', 'पूर्व राशि (1,5,9)', 'પૂર્વ રાશિ (1,5,9)', 'पूर्व रास (1,5,9)', 'পূর্ব রাশি (1,5,9)', 'ಪೂರ್ವ ರಾಶಿ (1,5,9)', 'கிழக்கு ராசி (1,5,9)', 'తూర్పు రాశి (1,5,9)'),
  west:  tr('West Zodiac (3,7,11)', 'पश्चिम राशि (3,7,11)', 'પશ્ચિમ રાશિ (3,7,11)', 'पश्चिम रास (3,7,11)', 'পশ্চিম রাশি (3,7,11)', 'ಪಶ್ಚಿಮ ರಾಶಿ (3,7,11)', 'மேற்கு ராசி (3,7,11)', 'పశ్చిమ రాశి (3,7,11)'),
  north: tr('North Zodiac (4,8,12)', 'उत्तर राशि (4,8,12)', 'ઉત્તર રાશિ (4,8,12)', 'उत्तर रास (4,8,12)', 'উত্তর রাশি (4,8,12)', 'ಉತ್ತರ ರಾಶಿ (4,8,12)', 'வடக்கு ராசி (4,8,12)', 'ఉత్తర రాశి (4,8,12)'),
  south: tr('South Zodiac (2,6,10)', 'दक्षिण राशि (2,6,10)', 'દક્ષિણ રાશિ (2,6,10)', 'दक्षिण रास (2,6,10)', 'দক্ষিণ রাশি (2,6,10)', 'ದಕ್ಷಿಣ ರಾಶಿ (2,6,10)', 'தெற்கு ராசி (2,6,10)', 'దక్షిణ రాశి (2,6,10)'),
};
export const directionGroupLabel = (key, lang = 'en') => pickLang(DIRECTION_GROUP_LABELS[key], lang) || key;

// Classical (pre-outer-planet) sign rulerships — same convention already
// used elsewhere in this app's Astro Vastu feature (Rahu/Ketu are nodes,
// not sign lords, so they're excluded here same as GRAHA_KEYS elsewhere).
export const SIGN_LORD = {
  1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
  7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
};

// Planet → activities this planet's house/area governs. Text taken verbatim
// from the client's reference report.
export const GENERAL_HOUSE_ACTIVITIES = {
  Sun:     ['Puja room'],
  Moon:    ['Water bodies', 'Support'],
  Mars:    ['Kitchen', 'Gym'],
  Mercury: ['Garden', 'Study table', 'Library', 'Book shelf'],
  Jupiter: ['Locker', 'Property Papers'],
  Venus:   ['Bed Room'],
  Saturn:  ['Toilet', 'Dustbin'],
};
export const GENERAL_HOUSE_ASC_LABEL = 'Main Door';

export const OFFICE_ACTIVITIES = {
  Sun:     ['Temple', 'Meditation Room', 'Managers'],
  Moon:    ['U/G or O/H Water Storage'],
  Mars:    ['Pantry', 'Boiler'],
  Mercury: ['Self Office', 'Reception', 'Accounts', 'Stairs', 'Garden'],
  Jupiter: ['Locker', 'Documents', 'Waiting Area', 'Cupboard'],
  Venus:   ['Finished Goods', 'Cash Counter', 'Cheque Books'],
  Saturn:  ['Raw Material Store', 'Toilet', 'Dustbin', 'Labour'],
};
export const OFFICE_ASC_LABEL = 'Main Entry / Plot Facing';

// Common activity-noun tokens shared by both lists above (Garden, Locker,
// Toilet, Dustbin repeat across them) — translated once and looked up by
// the raw English token, so both tables reuse the same map.
const ACTIVITY_LABELS = {
  'Puja room':               tr('Puja room', 'पूजा कक्ष', 'પૂજા ખંડ', 'पूजा कक्ष', 'পূজা ঘর', 'ಪೂಜಾ ಕೊಠಡಿ', 'பூஜை அறை', 'పూజ గది'),
  'Water bodies':            tr('Water bodies', 'जल निकाय', 'જળાશયો', 'जलाशये', 'জলাশয়', 'ಜಲಮೂಲಗಳು', 'நீர்நிலைகள்', 'నీటి వనరులు'),
  'Support':                 tr('Support', 'सहायता', 'ટેકો', 'आधार', 'সহায়তা', 'ಬೆಂಬಲ', 'ஆதரவு', 'మద్దతు'),
  'Kitchen':                 tr('Kitchen', 'रसोईघर', 'રસોડું', 'स्वयंपाकघर', 'রান্নাঘর', 'ಅಡುಗೆಮನೆ', 'சமையலறை', 'వంటగది'),
  'Gym':                     tr('Gym', 'व्यायामशाला', 'જિમ', 'व्यायामशाळा', 'জিম', 'ಜಿಮ್', 'உடற்பயிற்சி கூடம்', 'జిమ్'),
  'Garden':                  tr('Garden', 'बगीचा', 'બગીચો', 'बाग', 'বাগান', 'ತೋಟ', 'தோட்டம்', 'తోట'),
  'Study table':             tr('Study table', 'अध्ययन मेज़', 'અભ્યાસ ટેબલ', 'अभ्यास टेबल', 'পড়ার টেবিল', 'ಅಧ್ಯಯನ ಮೇಜು', 'படிப்பு மேசை', 'చదువు టేబుల్'),
  'Library':                 tr('Library', 'पुस्तकालय', 'પુસ્તકાલય', 'ग्रंथालय', 'গ্রন্থাগার', 'ಗ್ರಂಥಾಲಯ', 'நூலகம்', 'గ్రంథాలయం'),
  'Book shelf':              tr('Book shelf', 'पुस्तक अलमारी', 'પુસ્તક શેલ્ફ', 'पुस्तक कपाट', 'বইয়ের তাক', 'ಪುಸ್ತಕ ಕಪಾಟು', 'புத்தக அலமாரி', 'పుస్తకాల షెల్ఫ్'),
  'Locker':                  tr('Locker', 'लॉकर', 'લોકર', 'लॉकर', 'লকার', 'ಲಾಕರ್', 'லாக்கர்', 'లాకర్'),
  'Property Papers':         tr('Property Papers', 'संपत्ति के दस्तावेज़', 'મિલકતના દસ્તાવેજો', 'मालमत्तेची कागदपत्रे', 'সম্পত্তির কাগজপত্র', 'ಆಸ್ತಿ ದಾಖಲೆಗಳು', 'சொத்து ஆவணங்கள்', 'ఆస్తి పత్రాలు'),
  'Bed Room':                tr('Bed Room', 'शयनकक्ष', 'બેડરૂમ', 'शयनकक्ष', 'শোবার ঘর', 'ಮಲಗುವ ಕೋಣೆ', 'படுக்கையறை', 'పడకగది'),
  'Toilet':                  tr('Toilet', 'शौचालय', 'શૌચાલય', 'शौचालय', 'টয়লেট', 'ಶೌಚಾಲಯ', 'கழிப்பறை', 'మరుగుదొడ్డి'),
  'Dustbin':                 tr('Dustbin', 'कूड़ेदान', 'કચરાપેટી', 'कचरापेटी', 'ময়লার ঝুড়ি', 'ಕಸದ ಬುಟ್ಟಿ', 'குப்பைத் தொட்டி', 'చెత్త బుట్ట'),
  'Temple':                  tr('Temple', 'मंदिर', 'મંદિર', 'मंदिर', 'মন্দির', 'ದೇವಸ್ಥಾನ', 'கோவில்', 'దేవాలయం'),
  'Meditation Room':         tr('Meditation Room', 'ध्यान कक्ष', 'ધ્યાન ખંડ', 'ध्यान कक्ष', 'ধ্যান ঘর', 'ಧ್ಯಾನ ಕೊಠಡಿ', 'தியான அறை', 'ధ్యాన గది'),
  'Managers':                tr('Managers', 'प्रबंधक', 'મેનેજરો', 'व्यवस्थापक', 'ম্যানেজার', 'ವ್ಯವಸ್ಥಾಪಕರು', 'மேலாளர்கள்', 'మేనేజర్లు'),
  'U/G or O/H Water Storage': tr('U/G or O/H Water Storage', 'भूमिगत या ऊपरी जल भंडारण', 'ભૂગર્ભ કે ઓવરહેડ પાણી સંગ્રહ', 'भूमिगत किंवा ओव्हरहेड पाणी साठवण', 'ভূগর্ভস্থ বা ওভারহেড জল সঞ্চয়', 'ಭೂಗತ ಅಥವಾ ಮೇಲ್ಛಾವಣಿ ನೀರಿನ ಸಂಗ್ರಹ', 'நிலத்தடி அல்லது மேல்நிலை நீர் சேமிப்பு', 'భూగర్భ లేదా పైకప్పు నీటి నిల్వ'),
  'Pantry':                  tr('Pantry', 'पेंट्री', 'પેન્ટ્રી', 'पेंट्री', 'প্যান্ট্রি', 'ಪ್ಯಾಂಟ್ರಿ', 'பேண்ட்ரி', 'పాంట్రీ'),
  'Boiler':                  tr('Boiler', 'बॉयलर', 'બોઈલર', 'बॉयलर', 'বয়লার', 'ಬಾಯ್ಲರ್', 'பாய்லர்', 'బాయిలర్'),
  'Self Office':             tr('Self Office', 'व्यक्तिगत कार्यालय', 'અંગત ઓફિસ', 'स्वतःचे कार्यालय', 'নিজস্ব অফিস', 'ಸ್ವಂತ ಕಚೇರಿ', 'சொந்த அலுவலகம்', 'స్వంత కార్యాలయం'),
  'Reception':               tr('Reception', 'स्वागत कक्ष', 'રિસેપ્શન', 'स्वागत कक्ष', 'রিসেপশন', 'ಸ್ವಾಗತ ಕೊಠಡಿ', 'வரவேற்பு', 'రిసెప్షన్'),
  'Accounts':                tr('Accounts', 'लेखा विभाग', 'એકાઉન્ટ્સ', 'लेखा विभाग', 'হিসাব বিভাগ', 'ಲೆಕ್ಕಪತ್ರ ವಿಭಾಗ', 'கணக்குப் பிரிவு', 'అకౌంట్స్'),
  'Stairs':                  tr('Stairs', 'सीढ़ियाँ', 'સીડીઓ', 'जिने', 'সিঁড়ি', 'ಮೆಟ್ಟಿಲುಗಳು', 'படிக்கட்டுகள்', 'మెట్లు'),
  'Documents':               tr('Documents', 'दस्तावेज़', 'દસ્તાવેજો', 'कागदपत्रे', 'নথিপত্র', 'ದಾಖಲೆಗಳು', 'ஆவணங்கள்', 'పత్రాలు'),
  'Waiting Area':            tr('Waiting Area', 'प्रतीक्षा क्षेत्र', 'રાહ જોવાનો વિસ્તાર', 'प्रतीक्षा क्षेत्र', 'অপেক্ষার স্থান', 'ಕಾಯುವ ಪ್ರದೇಶ', 'காத்திருக்கும் பகுதி', 'వేచి ఉండే ప్రాంతం'),
  'Cupboard':                tr('Cupboard', 'अलमारी', 'કબાટ', 'कपाट', 'আলমারি', 'ಬೀರು', 'அலமாரி', 'అల్మారా'),
  'Finished Goods':          tr('Finished Goods', 'तैयार माल', 'તૈયાર માલ', 'तयार माल', 'সমাপ্ত পণ্য', 'ಸಿದ್ಧ ಸರಕುಗಳು', 'முடிக்கப்பட்ட பொருட்கள்', 'తయారైన సరుకులు'),
  'Cash Counter':            tr('Cash Counter', 'नकद काउंटर', 'રોકડ કાઉન્ટર', 'रोख काउंटर', 'নগদ কাউন্টার', 'ನಗದು ಕೌಂಟರ್', 'பண கவுண்டர்', 'నగదు కౌంటర్'),
  'Cheque Books':            tr('Cheque Books', 'चेकबुक', 'ચેકબુક', 'चेकबुक', 'চেক বই', 'ಚೆಕ್ ಪುಸ್ತಕಗಳು', 'காசோலை புத்தகங்கள்', 'చెక్కు పుస్తకాలు'),
  'Raw Material Store':      tr('Raw Material Store', 'कच्चा माल भंडार', 'કાચો માલ સ્ટોર', 'कच्चा माल साठा', 'কাঁচামালের গুদাম', 'ಕಚ್ಚಾ ಸಾಮಗ್ರಿ ಸಂಗ್ರಹ', 'மூலப்பொருள் கிடங்கு', 'ముడి సరుకుల గిడ్డంగి'),
  'Labour':                  tr('Labour', 'श्रमिक', 'મજૂરો', 'कामगार', 'শ্রমিক', 'ಕಾರ್ಮಿಕರು', 'தொழிலாளர்கள்', 'కార్మికులు'),
};
export const activityLabel = (activity, lang = 'en') => pickLang(ACTIVITY_LABELS[activity], lang) || activity;

const GENERAL_HOUSE_ASC_LABEL_TR = tr('Main Door', 'मुख्य द्वार', 'મુખ્ય દરવાજો', 'मुख्य दरवाजा', 'প্রধান দরজা', 'ಮುಖ್ಯ ಬಾಗಿಲು', 'முதன்மை கதவு', 'ప్రధాన ద్వారం');
const OFFICE_ASC_LABEL_TR = tr('Main Entry / Plot Facing', 'मुख्य प्रवेश / भूखंड की दिशा', 'મુખ્ય પ્રવેશ / પ્લોટ દિશા', 'मुख्य प्रवेश / भूखंडाची दिशा', 'প্রধান প্রবেশ / প্লট মুখ', 'ಮುಖ್ಯ ಪ್ರವೇಶ / ಪ್ಲಾಟ್ ಮುಖ', 'முதன்மை நுழைவு / மனை முகப்பு', 'ప్రధాన ప్రవేశం / ప్లాట్ ముఖద్వారం');
export const ascLabel = (mode, lang = 'en') => pickLang(mode === 'office' ? OFFICE_ASC_LABEL_TR : GENERAL_HOUSE_ASC_LABEL_TR, lang);

const sumSignsFor = (perSignValues, group) =>
  DIRECTION_GROUPS[group].signs.reduce((sum, signNum) => sum + (perSignValues[RASHI_EN[signNum - 1]] ?? 0), 0);

/**
 * Builds the 4-direction (East/West/North/South) mapping table for either
 * the General House or Office/Shop/Factory report, from a navamsha.in
 * Ashtakavarga API response (`output.bav`, `output.sarvashtakavarga`).
 *
 * @param {object} ashtakavargaOutput  The `output` object from
 *   POST /astro/ashtakavarga (i.e. `{ bav: {...}, sarvashtakavarga: {...} }`).
 * @param {number} lagnaRashiNum  1-12, the Ascendant's sign — used to look
 *   up the Lagna Lord for the ASC row (see the module-level note on why
 *   this is an approximation, not the source report's exact method).
 * @param {'house'|'office'} mode
 * @returns {Array<{ planet: string, activities: string[], east: number, west: number, north: number, south: number }>}
 */
export const buildActivityMapping = (ashtakavargaOutput, lagnaRashiNum, mode = 'house') => {
  const bav = ashtakavargaOutput?.bav || {};
  const sav = ashtakavargaOutput?.sarvashtakavarga || {};
  const activities = mode === 'office' ? OFFICE_ACTIVITIES : GENERAL_HOUSE_ACTIVITIES;
  const ascLabel = mode === 'office' ? OFFICE_ASC_LABEL : GENERAL_HOUSE_ASC_LABEL;

  const rows = Object.keys(activities).map((planet) => ({
    planet,
    activities: activities[planet],
    east:  sumSignsFor(bav[planet] || {}, 'east'),
    west:  sumSignsFor(bav[planet] || {}, 'west'),
    north: sumSignsFor(bav[planet] || {}, 'north'),
    south: sumSignsFor(bav[planet] || {}, 'south'),
  }));

  // ASC row — approximated via the Lagna Lord's own BAV (see module note).
  const lagnaLord = SIGN_LORD[lagnaRashiNum];
  if (lagnaLord && bav[lagnaLord]) {
    rows.push({
      planet: 'ASC',
      activities: [ascLabel],
      east:  sumSignsFor(bav[lagnaLord], 'east'),
      west:  sumSignsFor(bav[lagnaLord], 'west'),
      north: sumSignsFor(bav[lagnaLord], 'north'),
      south: sumSignsFor(bav[lagnaLord], 'south'),
      isApproximated: true,
    });
  }

  // Sarvastak_Varga (Lucky Direction) — straight from the API's own
  // combined-total field, grouped the same way.
  rows.push({
    planet: 'Sarvastak_Varga',
    activities: ['Lucky Direction'],
    east:  sumSignsFor(sav, 'east'),
    west:  sumSignsFor(sav, 'west'),
    north: sumSignsFor(sav, 'north'),
    south: sumSignsFor(sav, 'south'),
  });

  return rows;
};

// ── Hit Theory (Astro-Vastu directional aspect grids) ──────────────────────
// Reverse-engineered from the client's own reference report's worked
// examples, combined with navamsha.in's KP chart API (9 planet longitudes +
// 12 house cusp longitudes — see AstroApiService.js#getKpChart, proxied
// through the backend's /astro/kp-chart route). The angle math itself is
// standard classical astrological aspect geometry (conjunction, square,
// trine, opposition, etc. at fixed angles around the 360° circle) — not
// proprietary — applied with two things confirmed from the reference
// report's own examples rather than assumed:
//   1. A DIRECTED angle (target - source), not the shortest angular
//      separation — Sun→Mercury and Mercury→Sun show two different angles
//      in the reference report that sum to exactly 360° (e.g. 353.9° +
//      6.1°), which only a directed (asymmetric) angle produces.
//   2. An orb of ~8° — inferred by checking the reference report's actual
//      classified examples (e.g. 82.2°→SQR is 8° short of the exact 90°
//      Square) against every HIT_MASTER angle; not an explicitly published
//      number, so it's kept as one config value (HIT_CONFIG.orb) rather
//      than hard-coded throughout, in case the source software's official
//      orb is ever confirmed to differ.
export const HIT_MASTER = [
  { code: 'CONJ', angle: 0,   name: 'Conjunction',    category: 'neutral' },
  { code: 'SSXT', angle: 30,  name: 'Semi-sextile',   category: 'positive' },
  { code: 'SSQR', angle: 45,  name: 'Semi-square',    category: 'negative' },
  { code: 'SXT',  angle: 60,  name: 'Sextile',        category: 'positive' },
  { code: 'Q',    angle: 72,  name: 'Quintile',       category: 'positive' },
  { code: 'SQR',  angle: 90,  name: 'Square',         category: 'negative' },
  { code: 'TRN',  angle: 120, name: 'Trine',          category: 'positive' },
  { code: 'SSQU', angle: 135, name: 'Sesquiquadrate', category: 'negative' },
  { code: 'INC',  angle: 150, name: 'Inconjunct',     category: 'negative' },
  { code: 'OPN',  angle: 180, name: 'Opposition',     category: 'negative' },
];

export const HIT_CONFIG = { orb: 8 };

const HIT_NAME_LABELS = {
  CONJ: tr('Conjunction', 'संयोग', 'સંયોજન', 'संयोग', 'সংযোজন', 'ಸಂಯೋಜನೆ', 'இணைவு', 'సంయోగం'),
  SSXT: tr('Semi-sextile', 'अर्ध-षष्ठक', 'અર્ધ-ષષ્ઠક', 'अर्ध-षष्ठक', 'অর্ধ-ষষ্ঠক', 'ಅರ್ಧ-ಷಷ್ಠಕ', 'அரை-அறுகோணம்', 'అర్ధ-షష్ఠకం'),
  SSQR: tr('Semi-square', 'अर्ध-वर्ग', 'અર્ધ-વર્ગ', 'अर्ध-वर्ग', 'অর্ধ-বর্গ', 'ಅರ್ಧ-ಚೌಕ', 'அரை-சதுரம்', 'అర్ధ-చతురస్రం'),
  SXT:  tr('Sextile', 'षष्ठक', 'ષષ્ઠક', 'षष्ठक', 'ষষ্ঠক', 'ಷಷ್ಠಕ', 'அறுகோணம்', 'షష్ఠకం'),
  Q:    tr('Quintile', 'पंचक', 'પંચક', 'पंचक', 'পঞ্চক', 'ಪಂಚಕ', 'ஐங்கோணம்', 'పంచకం'),
  SQR:  tr('Square', 'वर्ग', 'વર્ગ', 'वर्ग', 'বর্গ', 'ಚೌಕ', 'சதுரம்', 'చతురస్రం'),
  TRN:  tr('Trine', 'त्रिकोण', 'ત્રિકોણ', 'त्रिकोण', 'ত্রিকোণ', 'ತ್ರಿಕೋನ', 'முக்கோணம்', 'త్రికోణం'),
  SSQU: tr('Sesquiquadrate', 'सेस्क्विक्वाड्रेट', 'સેસ્ક્વિક્વાડ્રેટ', 'सेस्क्विक्वाड्रेट', 'সেসকুইক্যাড্রেট', 'ಸೆಸ್ಕ್ವಿಕ್ವಾಡ್ರೇಟ್', 'செஸ்குய்குவாட்ரேட்', 'సెస్క్విక్వాడ్రేట్'),
  INC:  tr('Inconjunct', 'विषमकोण', 'વિષમકોણ', 'विषमकोण', 'বিষমকোণ', 'ವಿಷಮಕೋನ', 'விஷமகோணம்', 'విషమకోణం'),
  OPN:  tr('Opposition', 'प्रतियोग', 'પ્રતિયોગ', 'प्रतियोग', 'প্রতিযোগ', 'ಪ್ರತಿಯೋಗ', 'எதிர்நிலை', 'ప్రతియోగం'),
};
export const hitNameLabel = (code, lang = 'en') => pickLang(HIT_NAME_LABELS[code], lang) || code;

export const normalizeLongitude = (value) => ((Number(value) % 360) + 360) % 360;

/** target - source, directed (0-360°) — NOT the shortest angular separation. See file note above for why. */
export const directedAngle = (source, target) =>
  (normalizeLongitude(target) - normalizeLongitude(source) + 360) % 360;

/** Shortest distance around the circle between two angles — used only to measure closeness to a HIT_MASTER angle, never as the displayed angle itself. */
const circularDistance = (a, b) => {
  const diff = Math.abs(a - b);
  return Math.min(diff, 360 - diff);
};

/** Classifies one directed angle against HIT_MASTER within `orb` — returns the nearest match, or null if nothing is within orb. */
export const detectHit = (angle, orb = HIT_CONFIG.orb) => {
  let best = null;
  for (const hit of HIT_MASTER) {
    const error = circularDistance(angle, hit.angle);
    if (error <= orb && (best === null || error < best.orb)) {
      best = { code: hit.code, name: hit.name, exactAngle: hit.angle, orb: error, category: hit.category };
    }
  }
  return best;
};

/**
 * Full HIT pipeline for one source→target pair.
 * @returns {{ angle: number, displayAngle: number, hit: object|null }}
 */
export const calculateHit = (sourceLongitude, targetLongitude) => {
  const angle = directedAngle(sourceLongitude, targetLongitude);
  return { angle, displayAngle: Number(angle.toFixed(1)), hit: detectHit(angle) };
};

export const HIT_PLANETS = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];

// Column display order for the Planet→House grid — starts at House 2, ends
// at House 1 — matches the reference report's own column order. Internal
// calculation/storage always stays keyed 1-12 by actual house number; only
// the UI/PDF rendering loops over this array.
export const HIT_DISPLAY_HOUSES = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 1];

/** 9×9 Planet→Planet HIT grid. `grid[source][target]` is null on the diagonal, else a calculateHit() result. */
export const generatePlanetPlanetGrid = (planets) => {
  const grid = {};
  HIT_PLANETS.forEach((source) => {
    grid[source] = {};
    HIT_PLANETS.forEach((target) => {
      grid[source][target] = (source === target || planets[source] == null || planets[target] == null)
        ? null
        : calculateHit(planets[source], planets[target]);
    });
  });
  return grid;
};

/** Planet→House HIT grid, keyed by actual house number 1-12 (see HIT_DISPLAY_HOUSES for display column order). */
export const generatePlanetHouseGrid = (planets, cusps) => {
  const grid = {};
  HIT_PLANETS.forEach((planet) => {
    grid[planet] = {};
    for (let house = 1; house <= 12; house++) {
      grid[planet][house] = (planets[planet] == null || cusps[house] == null)
        ? null
        : calculateHit(planets[planet], cusps[house]);
    }
  });
  return grid;
};

// ── Zodiac metadata (for the Hit Theory report's house-column headers) ─────
// Standard classical 12-sign table: direction, ruling planet, element,
// modality — used to build the House column's DIR/Zodiac/Owner/Element/
// Modality/Rashi header row in the Planet→House grid.
// ── Language support for the D1 chart's OWN fixed vocabulary ───────────────
// The chart's actual numbers (Ascendant, planet-in-house placements, degrees)
// come from navamsha.in's API — that call never sends/receives a language
// setting, so those values only ever come back in English and there's
// nothing to translate there (they're structural/numeric, not prose,
// anyway). What IS fully within this app's own control — and wasn't wired
// to the language dropdown at all before this — is the FIXED text the app
// itself draws around that data: the compass-direction labels on the Vedic
// Vastu / AstroVastu chart variants, the Laal Kitaab wall names, and the
// Rashi (zodiac sign) names. Same tr()/pickLang() pattern as every other
// label in this file.
const COMPASS_PRIMARY_TO_DIRECTION_KEY = {
  N: 'North', S: 'South', E: 'East', W: 'West',
  NE: 'North-East', NW: 'North-West', SE: 'South-East', SW: 'South-West',
};
// Only the 8 primary/intercardinal points have a solid, unambiguous
// translated word already confirmed in every one of this app's 8 languages
// (DIRECTION_LABELS above). The finer 16-point-only codes (NNE/ENE/ESE/SSE/
// SSW/WSW/WNW/NNW) are left as their English abbreviation in every
// language — there's no reliably-sourced compound translation for those to
// draw from, and guessing one risks showing something wrong rather than
// just showing the (already fairly universal, technical-notation) code.
export const compassCodeLabel = (code, lang = 'en') => {
  const key = COMPASS_PRIMARY_TO_DIRECTION_KEY[code];
  return key ? pickLang(DIRECTION_LABELS[key], lang) : code;
};

const LAAL_KITAAB_WALL_LABELS = {
  'Eastern Wall':  tr('Eastern Wall', 'पूर्वी दीवार', 'પૂર્વ દિવાલ', 'पूर्व भिंत', 'পূর্ব দেয়াল', 'ಪೂರ್ವ ಗೋಡೆ', 'கிழக்கு சுவர்', 'తూర్పు గోడ'),
  'North Wall':    tr('North Wall', 'उत्तरी दीवार', 'ઉત્તર દિવાલ', 'उत्तर भिंत', 'উত্তর দেয়াল', 'ಉತ್ತರ ಗೋಡೆ', 'வடக்கு சுவர்', 'ఉత్తర గోడ'),
  'West Wall':     tr('West Wall', 'पश्चिमी दीवार', 'પશ્ચિમ દિવાલ', 'पश्चिम भिंत', 'পশ্চিম দেয়াল', 'ಪಶ್ಚಿಮ ಗೋಡೆ', 'மேற்கு சுவர்', 'పశ్చిమ గోడ'),
  'Southern Wall': tr('Southern Wall', 'दक्षिणी दीवार', 'દક્ષિણ દિવાલ', 'दक्षिण भिंत', 'দক্ষিণ দেয়াল', 'ದಕ್ಷಿಣ ಗೋಡೆ', 'தெற்கு சுவர்', 'దక్షిణ గోడ'),
  'Center':        tr('Center', 'केंद्र', 'કેન્દ્ર', 'केंद्र', 'কেন্দ্র', 'ಕೇಂದ್ರ', 'மையம்', 'కేంద్రం'),
};
// Laal Kitaab's diagonal cells ('North-East' etc.) already match
// DIRECTION_LABELS' own keys verbatim — only its "…Wall"/"Center" phrasing
// needed a dictionary of its own.
export const laalKitaabLabel = (raw, lang = 'en') =>
  pickLang(DIRECTION_LABELS[raw] || LAAL_KITAAB_WALL_LABELS[raw], lang) || raw;

const RASHI_NAME_LABELS = {
  Aries:       tr('Aries', 'मेष', 'મેષ', 'मेष', 'মেষ', 'ಮೇಷ', 'மேஷம்', 'మేషం'),
  Taurus:      tr('Taurus', 'वृषभ', 'વૃષભ', 'वृषभ', 'বৃষ', 'ವೃಷಭ', 'ரிஷபம்', 'వృషభం'),
  Gemini:      tr('Gemini', 'मिथुन', 'મિથુન', 'मिथुन', 'মিথুন', 'ಮಿಥುನ', 'மிதுனம்', 'మిథునం'),
  Cancer:      tr('Cancer', 'कर्क', 'કર્ક', 'कर्क', 'কর্কট', 'ಕರ್ಕ', 'கடகம்', 'కర్కాటకం'),
  Leo:         tr('Leo', 'सिंह', 'સિંહ', 'सिंह', 'সিংহ', 'ಸಿಂಹ', 'சிம்மம்', 'సింహం'),
  Virgo:       tr('Virgo', 'कन्या', 'કન્યા', 'कन्या', 'কন্যা', 'ಕನ್ಯಾ', 'கன்னி', 'కన్య'),
  Libra:       tr('Libra', 'तुला', 'તુલા', 'तुला', 'তুলা', 'ತುಲಾ', 'துலாம்', 'తుల'),
  Scorpio:     tr('Scorpio', 'वृश्चिक', 'વૃશ્ચિક', 'वृश्चिक', 'বৃশ্চিক', 'ವೃಶ್ಚಿಕ', 'விருச்சிகம்', 'వృశ్చికం'),
  Sagittarius: tr('Sagittarius', 'धनु', 'ધનુ', 'धनु', 'ধনু', 'ಧನು', 'தனுசு', 'ధనుస్సు'),
  Capricorn:   tr('Capricorn', 'मकर', 'મકર', 'मकर', 'মকর', 'ಮಕರ', 'மகரம்', 'మకరం'),
  Aquarius:    tr('Aquarius', 'कुम्भ', 'કુંભ', 'कुंभ', 'কুম্ভ', 'ಕುಂಭ', 'கும்பம்', 'కుంభం'),
  Pisces:      tr('Pisces', 'मीन', 'મીન', 'मीन', 'মীন', 'ಮೀನ', 'மீனம்', 'మీనం'),
};
// `enName` is a RASHI_EN entry ("Aries".."Pisces") — the same English name
// this app's RASHIS array already carries as its `.en` field.
export const rashiLabel = (enName, lang = 'en') => pickLang(RASHI_NAME_LABELS[enName], lang) || enName;

// Planet (Graha) names — shown as raw literal strings ("Sun","Moon"...) all
// over the Kundali report tables (Vimsottari lord, Plant Remedy rows, Hit
// Theory grid headers, Activity Mapping rows) even though the DATA next to
// them (activities/plants/directions) was already translated — leaving the
// planet name itself in English made every one of those rows look half
// translated. `GRAHAS`/`HIT_PLANETS` elsewhere already use these exact
// English keys ('Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn',
// 'Rahu','Ketu'), so this dict is keyed the same way.
const PLANET_NAME_LABELS = {
  Sun:     tr('Sun', 'सूर्य', 'સૂર્ય', 'सूर्य', 'সূর্য', 'ಸೂರ್ಯ', 'சூரியன்', 'సూర్యుడు'),
  Moon:    tr('Moon', 'चंद्र', 'ચંદ્ર', 'चंद्र', 'চন্দ্র', 'ಚಂದ್ರ', 'சந்திரன்', 'చంద్రుడు'),
  Mars:    tr('Mars', 'मंगल', 'મંગળ', 'मंगळ', 'মঙ্গল', 'ಮಂಗಳ', 'செவ்வாய்', 'కుజుడు'),
  Mercury: tr('Mercury', 'बुध', 'બુધ', 'बुध', 'বুধ', 'ಬುಧ', 'புதன்', 'బుధుడు'),
  Jupiter: tr('Jupiter', 'बृहस्पति', 'ગુરુ', 'गुरू', 'বৃহস্পতি', 'ಗುರು', 'குரு', 'గురుడు'),
  Venus:   tr('Venus', 'शुक्र', 'શુક્ર', 'शुक्र', 'শুক্র', 'ಶುಕ್ರ', 'சுக்ரன்', 'శుక్రుడు'),
  Saturn:  tr('Saturn', 'शनि', 'શનિ', 'शनि', 'শনি', 'ಶನಿ', 'சனி', 'శని'),
  Rahu:    tr('Rahu', 'राहु', 'રાહુ', 'राहू', 'রাহু', 'ರಾಹು', 'ராகு', 'రాహువు'),
  Ketu:    tr('Ketu', 'केतु', 'કેતુ', 'केतू', 'কেতু', 'ಕೇತು', 'கேது', 'కేతువు'),
};
export const planetLabel = (planet, lang = 'en') => pickLang(PLANET_NAME_LABELS[planet], lang) || planet;

// Kundali report section titles (the card headings every report section now
// stacks under — see VastuPersonsGateway.jsx's KundaliReportsPanel) and the
// table column headers inside them. Keyed by the exact English text used as
// the lookup key throughout that file, same pattern as directionLabel etc.
const KUNDALI_TEXT_LABELS = {
  'Kundali': tr('Kundali', 'कुंडली', 'કુંડળી', 'कुंडली', 'কুণ্ডলী', 'ಕುಂಡಲಿ', 'ஜாதகம்', 'జాతకం'),
  'Vimsottari Table': tr('Vimsottari Table', 'विंशोत्तरी तालिका', 'વિંશોત્તરી કોષ્ટક', 'विंशोत्तरी तक्ता', 'বিংশোত্তরী তালিকা', 'ವಿಂಶೋತ್ತರಿ ಕೋಷ್ಟಕ', 'விம்சோத்தரி அட்டவணை', 'వింశోత్తరి పట్టిక'),
  'Navatara Chakra': tr('Navatara Chakra', 'नवतारा चक्र', 'નવતારા ચક્ર', 'नवतारा चक्र', 'নবতারা চক্র', 'ನವತಾರಾ ಚಕ್ರ', 'நவதாரா சக்கரம்', 'నవతారా చక్రం'),
  'Ruling Deities': tr('Ruling Deities', 'शासक देवता', 'શાસક દેવતા', 'शासक देवता', 'শাসক দেবতা', 'ಆಳ್ವಿಕೆ ದೇವತೆ', 'ஆளும் தெய்வம்', 'పాలక దేవత'),
  'Astro-Vastu Plant Remedy': tr('Astro-Vastu Plant Remedy', 'ज्योतिष-वास्तु पौधा उपचार', 'જ્યોતિષ-વાસ્તુ છોડ ઉપચાર', 'ज्योतिष-वास्तू रोप उपचार', 'জ্যোতিষ-বাস্তু উদ্ভিদ প্রতিকার', 'ಜ್ಯೋತಿಷ-ವಾಸ್ತು ಸಸ್ಯ ಪರಿಹಾರ', 'ஜோதிட-வாஸ்து தாவர பரிகாரம்', 'జ్యోతిష-వాస్తు మొక్క నివారణ'),
  'General House Activity Mapping': tr('General House Activity Mapping', 'सामान्य भवन गतिविधि मानचित्रण', 'સામાન્ય મકાન પ્રવૃત્તિ મેપિંગ', 'सामान्य घर क्रियाकलाप मॅपिंग', 'সাধারণ বাড়ি কার্যকলাপ ম্যাপিং', 'ಸಾಮಾನ್ಯ ಮನೆ ಚಟುವಟಿಕೆ ಮ್ಯಾಪಿಂಗ್', 'பொது வீட்டு செயல்பாடு வரைபடம்', 'సాధారణ ఇంటి కార్యకలాప మ్యాపింగ్'),
  'Office/Shop/Factory Layout Mapping': tr('Office/Shop/Factory Layout Mapping', 'कार्यालय/दुकान/फैक्ट्री लेआउट मानचित्रण', 'ઓફિસ/દુકાન/ફેક્ટરી લેઆઉટ મેપિંગ', 'कार्यालय/दुकान/कारखाना मांडणी मॅपिंग', 'অফিস/দোকান/কারখানা লেআউট ম্যাপিং', 'ಕಚೇರಿ/ಅಂಗಡಿ/ಕಾರ್ಖಾನೆ ವಿನ್ಯಾಸ ಮ್ಯಾಪಿಂಗ್', 'அலுவலகம்/கடை/தொழிற்சாலை அமைப்பு வரைபடம்', 'కార్యాలయం/దుకాణం/ఫ్యాక్టరీ లేఅవుట్ మ్యాపింగ్'),
  'Hit Theory': tr('Hit Theory', 'हिट थ्योरी', 'હિટ થિયરી', 'हिट थिअरी', 'হিট থিওরি', 'ಹಿಟ್ ಥಿಯರಿ', 'ஹிட் தியரி', 'హిట్ థియరీ'),
  'Best Direction Table': tr('Best Direction Table', 'सर्वोत्तम दिशा तालिका', 'શ્રેષ્ઠ દિશા કોષ્ટક', 'सर्वोत्तम दिशा तक्ता', 'সেরা দিক তালিকা', 'ಉತ್ತಮ ದಿಕ್ಕಿನ ಕೋಷ್ಟಕ', 'சிறந்த திசை அட்டவணை', 'ఉత్తమ దిశ పట్టిక'),
  'General Suggestions & Remedies': tr('General Suggestions & Remedies', 'सामान्य सुझाव और उपाय', 'સામાન્ય સૂચનો અને ઉપાયો', 'सामान्य सूचना आणि उपाय', 'সাধারণ পরামর্শ ও প্রতিকার', 'ಸಾಮಾನ್ಯ ಸಲಹೆಗಳು ಮತ್ತು ಪರಿಹಾರಗಳು', 'பொது ஆலோசனைகள் & பரிகாரங்கள்', 'సాధారణ సూచనలు & నివారణలు'),
  'Analysis Notes': tr('Analysis Notes', 'विश्लेषण टिप्पणियाँ', 'વિશ્લેષણ નોંધો', 'विश्लेषण नोंदी', 'বিশ্লেষণ নোট', 'ವಿಶ್ಲೇಷಣೆ ಟಿಪ್ಪಣಿಗಳು', 'பகுப்பாய்வு குறிப்புகள்', 'విశ్లేషణ గమనికలు'),
  // Table column headers reused across several of the sections above.
  'Planet': tr('Planet', 'ग्रह', 'ગ્રહ', 'ग्रह', 'গ্রহ', 'ಗ್ರಹ', 'கிரகம்', 'గ్రహం'),
  'Start': tr('Start', 'प्रारंभ', 'શરૂઆત', 'सुरुवात', 'শুরু', 'ಪ್ರಾರಂಭ', 'தொடக்கம்', 'ప్రారంభం'),
  'End': tr('End', 'समाप्ति', 'અંત', 'समाप्ती', 'শেষ', 'ಅಂತ್ಯ', 'முடிவு', 'ముగింపు'),
  'S.No.': tr('S.No.', 'क्र.सं.', 'ક્રમ', 'अ.क्र.', 'ক্র.নং', 'ಕ್ರ.ಸಂ', 'வ.எண்', 'క్ర.సం'),
  'Navatara': tr('Navatara', 'नवतारा', 'નવતારા', 'नवतारा', 'নবতারা', 'ನವತಾರಾ', 'நவதாரா', 'నవతారా'),
  'Lord': tr('Lord', 'स्वामी', 'સ્વામી', 'स्वामी', 'অধিপতি', 'ಒಡೆಯ', 'அதிபதி', 'అధిపతి'),
  'Nakshatra 1': tr('Nakshatra 1', 'नक्षत्र 1', 'નક્ષત્ર 1', 'नक्षत्र 1', 'নক্ষত্র ১', 'ನಕ್ಷತ್ರ 1', 'நட்சத்திரம் 1', 'నక్షత్రం 1'),
  'Nakshatra 2': tr('Nakshatra 2', 'नक्षत्र 2', 'નક્ષત્ર 2', 'नक्षत्र 2', 'নক্ষত্র ২', 'ನಕ್ಷತ್ರ 2', 'நட்சத்திரம் 2', 'నక్షత్రం 2'),
  'Nakshatra 3': tr('Nakshatra 3', 'नक्षत्र 3', 'નક્ષત્ર 3', 'नक्षत्र 3', 'নক্ষত্র ৩', 'ನಕ್ಷತ್ರ 3', 'நட்சத்திரம் 3', 'నక్షత్రం 3'),
  'Birth Nakshatra': tr('Birth Nakshatra', 'जन्म नक्षत्र', 'જન્મ નક્ષત્ર', 'जन्म नक्षत्र', 'জন্ম নক্ষত্র', 'ಜನ್ಮ ನಕ್ಷತ್ರ', 'பிறப்பு நட்சத்திரம்', 'జన్మ నక్షత్రం'),
  'Ruling Deity': tr('Ruling Deity', 'शासक देवता', 'શાસક દેવતા', 'शासक देवता', 'শাসক দেবতা', 'ಆಳ್ವಿಕೆ ದೇವತೆ', 'ஆளும் தெய்வம்', 'పాలక దేవత'),
  'Remedy Plant / Tree': tr('Remedy Plant / Tree', 'उपचार पौधा / वृक्ष', 'ઉપચાર છોડ / વૃક્ષ', 'उपचार रोप / झाड', 'প্রতিকার উদ্ভিদ / গাছ', 'ಪರಿಹಾರ ಸಸ್ಯ / ಮರ', 'பரிகார தாவரம் / மரம்', 'నివారణ మొక్క / చెట్టు'),
  'Vastu Direction': tr('Vastu Direction', 'वास्तु दिशा', 'વાસ્તુ દિશા', 'वास्तू दिशा', 'বাস্তু দিক', 'ವಾಸ್ತು ದಿಕ್ಕು', 'வாஸ்து திசை', 'వాస్తు దిశ'),
  'Nakshatra': tr('Nakshatra', 'नक्षत्र', 'નક્ષત્ર', 'नक्षत्र', 'নক্ষত্র', 'ನಕ್ಷತ್ರ', 'நட்சத்திரம்', 'నక్షత్రం'),
  'Best Direction': tr('Best Direction', 'सर्वोत्तम दिशा', 'શ્રેષ્ઠ દિશા', 'सर्वोत्तम दिशा', 'সেরা দিক', 'ಉತ್ತಮ ದಿಕ್ಕು', 'சிறந்த திசை', 'ఉత్తమ దిశ'),
  'Source \\ Target': tr('Source \\ Target', 'स्रोत \\ लक्ष्य', 'સ્રોત \\ લક્ષ્ય', 'स्रोत \\ लक्ष्य', 'উৎস \\ লক্ষ্য', 'ಮೂಲ \\ ಗುರಿ', 'மூலம் \\ இலக்கு', 'మూలం \\ లక్ష్యం'),
  'Direction': tr('Direction', 'दिशा', 'દિશા', 'दिशा', 'দিক', 'ದಿಕ್ಕು', 'திசை', 'దిశ'),
  'Zodiac': tr('Zodiac', 'राशि', 'રાશિ', 'रास', 'রাশি', 'ರಾಶಿ', 'ராசி', 'రాశి'),
  'Owner': tr('Owner', 'स्वामी', 'સ્વામી', 'स्वामी', 'অধিপতি', 'ಒಡೆಯ', 'அதிபதி', 'అధిపతి'),
  'House': tr('House', 'भाव', 'ભાવ', 'भाव', 'ভাব', 'ಭಾವ', 'பாவம்', 'భావం'),
  'Activity': tr('Activity', 'गतिविधि', 'પ્રવૃત્તિ', 'क्रियाकलाप', 'কার্যকলাপ', 'ಚಟುವಟಿಕೆ', 'செயல்பாடு', 'కార్యకలాపం'),
  // PDF-only combined page titles (the live editor shows each of these as
  // its own separate card, so it never needed a combined phrase — the PDF
  // merges several short tables onto one shared page and needs one title
  // for that whole page).
  'Vimsottari & Navatara Chakra': tr('Vimsottari & Navatara Chakra', 'विंशोत्तरी और नवतारा चक्र', 'વિંશોત્તરી અને નવતારા ચક્ર', 'विंशोत्तरी आणि नवतारा चक्र', 'বিংশোত্তরী ও নবতারা চক্র', 'ವಿಂಶೋತ್ತರಿ ಮತ್ತು ನವತಾರಾ ಚಕ್ರ', 'விம்சோத்தரி & நவதாரா சக்கரம்', 'వింశోత్తరి & నవతారా చక్రం'),
  'Nakshatra, Deity & Plant Remedy': tr('Nakshatra, Deity & Plant Remedy', 'नक्षत्र, देवता और पौधा उपचार', 'નક્ષત્ર, દેવતા અને છોડ ઉપચાર', 'नक्षत्र, देवता आणि रोप उपचार', 'নক্ষত্র, দেবতা ও উদ্ভিদ প্রতিকার', 'ನಕ್ಷತ್ರ, ದೇವತೆ ಮತ್ತು ಸಸ್ಯ ಪರಿಹಾರ', 'நட்சத்திரம், தெய்வம் & தாவர பரிகாரம்', 'నక్షత్రం, దేవత & మొక్క నివారణ'),
  'Hit Theory — Planet to Planet': tr('Hit Theory — Planet to Planet', 'हिट थ्योरी — ग्रह से ग्रह', 'હિટ થિયરી — ગ્રહથી ગ્રહ', 'हिट थिअरी — ग्रहापासून ग्रह', 'হিট থিওরি — গ্রহ থেকে গ্রহ', 'ಹಿಟ್ ಥಿಯರಿ — ಗ್ರಹದಿಂದ ಗ್ರಹ', 'ஹிட் தியரி — கிரகத்திலிருந்து கிரகம்', 'హిట్ థియరీ — గ్రహం నుండి గ్రహం'),
  'Hit Theory — Planet to House': tr('Hit Theory — Planet to House', 'हिट थ्योरी — ग्रह से भाव', 'હિટ થિયરી — ગ્રહથી ભાવ', 'हिट थिअरी — ग्रहापासून भाव', 'হিট থিওরি — গ্রহ থেকে ভাব', 'ಹಿಟ್ ಥಿಯರಿ — ಗ್ರಹದಿಂದ ಭಾವ', 'ஹிட் தியரி — கிரகத்திலிருந்து பாவம்', 'హిట్ థియరీ — గ్రహం నుండి భావం'),
  'House & Office Activity Mapping': tr('House & Office Activity Mapping', 'भवन और कार्यालय गतिविधि मानचित्रण', 'મકાન અને ઓફિસ પ્રવૃત્તિ મેપિંગ', 'घर आणि कार्यालय क्रियाकलाप मॅपिंग', 'বাড়ি ও অফিস কার্যকলাপ ম্যাপিং', 'ಮನೆ ಮತ್ತು ಕಚೇರಿ ಚಟುವಟಿಕೆ ಮ್ಯಾಪಿಂಗ್', 'வீடு & அலுவலக செயல்பாடு வரைபடம்', 'ఇల్లు & కార్యాలయం కార్యకలాప మ్యాపింగ్'),
};
export const kundaliTextLabel = (text, lang = 'en') => pickLang(KUNDALI_TEXT_LABELS[text], lang) || text;

export const ZODIAC_MASTER = {
  1:  { sign: 'Aries',       direction: 'E',   owner: 'Mars',    element: 'Fire',  modality: 'Movable' },
  2:  { sign: 'Taurus',      direction: 'ENE', owner: 'Venus',   element: 'Earth', modality: 'Fixed' },
  3:  { sign: 'Gemini',      direction: 'NNE', owner: 'Mercury', element: 'Air',   modality: 'Dual' },
  4:  { sign: 'Cancer',      direction: 'N',   owner: 'Moon',    element: 'Water', modality: 'Movable' },
  5:  { sign: 'Leo',         direction: 'NNW', owner: 'Sun',     element: 'Fire',  modality: 'Fixed' },
  6:  { sign: 'Virgo',       direction: 'NW',  owner: 'Mercury', element: 'Earth', modality: 'Dual' },
  7:  { sign: 'Libra',       direction: 'WSW', owner: 'Venus',   element: 'Air',   modality: 'Movable' },
  8:  { sign: 'Scorpio',     direction: 'W',   owner: 'Mars',    element: 'Water', modality: 'Fixed' },
  9:  { sign: 'Sagittarius', direction: 'SSW', owner: 'Jupiter', element: 'Fire',  modality: 'Dual' },
  10: { sign: 'Capricorn',   direction: 'S',   owner: 'Saturn',  element: 'Earth', modality: 'Movable' },
  11: { sign: 'Aquarius',    direction: 'SSE', owner: 'Saturn',  element: 'Air',   modality: 'Fixed' },
  12: { sign: 'Pisces',      direction: 'SE',  owner: 'Jupiter', element: 'Water', modality: 'Dual' },
};

export const getZodiacFromLongitude = (longitude) => {
  const norm = normalizeLongitude(longitude);
  const rashiNo = Math.floor(norm / 30) + 1;
  const degreeInSign = norm % 30;
  return { rashiNo, degreeInSign, ...ZODIAC_MASTER[rashiNo] };
};

export default {
  NAKSHATRAS, getNakshatra, computeNavataraChakra,
  RASHI_EN, DIRECTION_GROUPS, SIGN_LORD,
  GENERAL_HOUSE_ACTIVITIES, GENERAL_HOUSE_ASC_LABEL,
  OFFICE_ACTIVITIES, OFFICE_ASC_LABEL,
  buildActivityMapping,
  HIT_MASTER, HIT_CONFIG, HIT_PLANETS, HIT_DISPLAY_HOUSES,
  normalizeLongitude, directedAngle, detectHit, calculateHit,
  generatePlanetPlanetGrid, generatePlanetHouseGrid,
  ZODIAC_MASTER, getZodiacFromLongitude,
  deityLabel, directionLabel, plantLabel, navataraLabel,
  activityLabel, ascLabel, directionGroupLabel, hitNameLabel,
  compassCodeLabel, laalKitaabLabel, rashiLabel,
  planetLabel, kundaliTextLabel,
};
